import useTranslation from 'hooks/useTranslation/useTranslation';
import { getVisitorGroupsThatHaveLocationAssigned, getVisitorGroupsWithAccessToDevice } from 'utils/Lock/Lock';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useDeviceDetails from 'v2/hooks/useDeviceDetails/useDeviceDetails';

const useDeviceGroupsInfo = (deviceId: string) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const { device, deviceLoading, visitorGroups, visitorGroupsLoading } = useDeviceDetails({
    handleFetchError,
    translation,
    deviceId
  });

  const isLoading = deviceLoading || device === undefined || visitorGroupsLoading || visitorGroups === undefined;

  if (isLoading) return { isLoading: true as const };

  const deviceGroups = (device?.locations || []).map((deviceGroup) => ({
    id: deviceGroup.id,
    name: deviceGroup.name,
    isIncomplete: !getVisitorGroupsThatHaveLocationAssigned(deviceGroup.id, visitorGroups).some(
      (visitorGroup) => visitorGroup.members.length > 0
    )
  }));

  const userGroupsWithAccess = getVisitorGroupsWithAccessToDevice(device.id, deviceGroups, visitorGroups);

  // A device is accessible if it has at least one complete device group or one complete visitor group.
  // A device group is complete if it has at least one visitor group with members.
  // A visitor group is complete if it has members.
  // Error alerts only show when the device is inaccessible — otherwise show an informational message.
  const isDeviceAccessible =
    deviceGroups.some((deviceGroup) => !deviceGroup.isIncomplete) ||
    userGroupsWithAccess.some((visitorGroup) => !visitorGroup.isIncomplete);

  return {
    isLoading: false as const,
    deviceId: device.id,
    deviceGroups,
    userGroupsWithAccess,
    isDeviceAccessible
  };
};

export default useDeviceGroupsInfo;
